<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NEmpty, useMessage } from 'naive-ui'
import { RouterLink } from 'vue-router'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import type { ChatMessage } from '../stores/agent'
import { isInterAgentTurn, sourceLabel } from '../lib/interAgentTurn'

// The /agent conversation transcript, extracted from AgentView so the
// view stays under the file-size limit. Owns its own scroll container and the
// "stick to the bottom unless the user scrolled up" behaviour, and renders
// your turns as right-aligned brand bubbles vs the agent's left-aligned
// markdown output so the conversation is scannable at a glance.
const props = defineProps<{
  messages: ChatMessage[]
  agentRunning: boolean
  hasActiveThread: boolean
  liveStatus: string | null
}>()

// Within this many px of the bottom counts as "stuck" — auto-scroll only then.
const NEAR_BOTTOM_PX = 80

const wikiMarkdown = useWikiMarkdown()
const message = useMessage()
const { formatDate, formatAbsoluteDate } = useFormattedDate()
// Ticks so a bubble's relative timestamp ("just now" → "1 min ago" → …)
// keeps advancing while the transcript sits idle, instead of freezing at
// whatever it read on the last render caused by a new message (#1438).
const now = useNow()
const transcriptEl = ref<HTMLElement | null>(null)
// Only the owner's own typed turns are right-aligned brand bubbles; turns
// another agent delivered (#1837) and system notes must not read as theirs.
function isOwnTurn(m: ChatMessage): boolean {
  return m.role === 'you' && !isInterAgentTurn(m)
}
const messagesEmpty = computed(() => props.messages.length === 0)

interface MessageGroup {
  message: ChatMessage
  repeatCount: number
}

// Collapse a run of consecutive, same-role turns whose text is identical
// (trimmed) into one bubble with a repeat count. A background job the agent
// polls (e.g. via the Monitor tool) can make it reply with the exact same
// short status line many times in a row, which reads as noise rather than
// as that many distinct turns (#1442). Keeps the most recent occurrence's
// metadata (timestamp/context/cost) since that's the freshest read.
const groupedMessages = computed<MessageGroup[]>(() => {
  const groups: MessageGroup[] = []
  for (const m of props.messages) {
    const last = groups[groups.length - 1]
    if (last && last.message.role === m.role && last.message.text.trim() === m.text.trim()) {
      last.message = m
      last.repeatCount += 1
    } else {
      groups.push({ message: m, repeatCount: 1 })
    }
  }
  return groups
})

// Compact "context so far" label for the badge below an agent turn — the
// visibility issue #886 asked for (no signal today for when to run /compact).
function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`
  return String(n)
}

// Format cents as $X.XX (mirrors UsageView's formatUsd). Below $0.01
// collapses to "<$0.01" so a cheap turn doesn't read as "$0.00" (issue #654).
function formatCost(cents: number): string {
  if (cents === 0) return '$0.00'
  if (cents < 1) return '<$0.01'
  return `$${(cents / 100).toFixed(2)}`
}

// Copy an agent turn's raw markdown to the clipboard. Long agent
// responses are tedious to text-select inside a scrolling log.
async function copyMessage(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    message.success('Copied to clipboard')
  } catch {
    message.warning('Clipboard not available — select and copy manually')
  }
}

function nearBottom(): boolean {
  const el = transcriptEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
}

// Auto-scroll on new output / run state, but only if the user was already at
// the bottom — don't yank them down while they're reading earlier output.
watch(
  () => [props.messages.length, props.agentRunning, props.liveStatus] as const,
  () => {
    const stick = nearBottom()
    nextTick(() => {
      if (stick && transcriptEl.value) transcriptEl.value.scrollTop = transcriptEl.value.scrollHeight
    })
  },
)
</script>

<template>
  <div
    ref="transcriptEl"
    class="flex-1 min-h-0 overflow-y-auto pr-1 flex flex-col gap-2"
    role="log"
    aria-live="polite"
    aria-atomic="false"
    data-testid="agent-transcript"
  >
    <NEmpty
      v-if="messagesEmpty && !agentRunning"
      size="small"
      class="my-6"
      :description="hasActiveThread ? 'No output yet for this session' : 'Start a new session below'"
    />
    <!-- Your turns are right-aligned brand-tinted bubbles; agent output is a
         left-aligned neutral bubble, so the conversation is scannable at a
         glance. The sr-only label keeps the speaker clear for screen
         readers since alignment / colour aren't announced. -->
    <div
      v-for="(g, i) in groupedMessages"
      :key="i"
      class="flex"
      :class="g.message.role === 'system' ? 'justify-center' : isOwnTurn(g.message) ? 'justify-end' : 'justify-start'"
      data-testid="agent-message"
    >
      <div
        v-if="g.message.role === 'system'"
        class="max-w-[85%] rounded-lg px-3 py-1 text-xs italic bg-amber-500/10 border border-amber-500/30 text-amber-200/90"
        data-testid="agent-message-system"
      >
        <span class="sr-only">System note:</span>
        {{ g.message.text }}
      </div>
      <div
        v-else
        class="group max-w-[85%] rounded-xl px-3 py-2 text-sm break-words"
        :class="
          isOwnTurn(g.message)
            ? 'bg-brand-primary/10 border border-brand-primary/30 text-white'
            : isInterAgentTurn(g.message)
              ? 'bg-sky-500/10 border border-sky-400/30 text-white/90'
              : 'bg-white/5 border border-white/10 text-white/90'
        "
        :data-testid="
          isOwnTurn(g.message)
            ? 'agent-message-you'
            : isInterAgentTurn(g.message)
              ? 'agent-message-inter-agent'
              : 'agent-message-agent'
        "
      >
        <!-- Inter-agent badge (#1837): who signed the turn and, for a channel
             turn, a link to the channel feed (or "deleted channel" once the
             channel is gone) — so it is never mistaken for an owner message. -->
        <div
          v-if="isInterAgentTurn(g.message)"
          class="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-sky-200/80"
          data-testid="inter-agent-badge"
        >
          <span class="font-medium">{{ g.message.sender_name }}</span>
          <span class="text-white/40">· {{ sourceLabel(g.message.source) }}</span>
          <RouterLink
            v-if="g.message.channel"
            :to="{ path: '/channels', query: { channel: String(g.message.channel.id) } }"
            class="underline"
            data-testid="inter-agent-channel-link"
            >#{{ g.message.channel.name }}</RouterLink
          >
          <span v-else-if="g.message.channel_deleted" class="italic" data-testid="inter-agent-channel-deleted"
            >deleted channel</span
          >
        </div>
        <span class="sr-only">{{ isOwnTurn(g.message) ? 'You said:' : 'Agent said:' }}</span>
        <span v-if="g.message.role === 'you'" class="whitespace-pre-wrap">{{ g.message.text }}</span>
        <article v-else class="wiki-markdown overflow-x-auto" v-html="wikiMarkdown.render(g.message.text)" />

        <!-- Per-turn footer: a subtle relative timestamp (both roles), a
             repeat count when this bubble collapsed a run of identical
             turns (#1442), the turn's context size on agent turns (#886 —
             visibility into when to run /compact), and, on agent turns, a
             Copy affordance — hover-revealed on desktop, always visible on
             mobile (matches the lint diff actions). -->
        <div
          v-if="g.message.created_at || g.message.role === 'agent'"
          class="mt-1 flex items-center gap-3 text-[11px] text-white/35"
          :class="isOwnTurn(g.message) ? 'justify-end' : 'justify-between'"
        >
          <div class="flex items-center gap-2">
            <time
              v-if="g.message.created_at"
              :datetime="g.message.created_at"
              :title="formatAbsoluteDate(g.message.created_at)"
              class="tabular-nums"
              data-testid="agent-message-time"
              >{{ formatDate(g.message.created_at, now) }}</time
            >
            <span v-else aria-hidden="true" />
            <span
              v-if="g.repeatCount > 1"
              class="tabular-nums"
              :title="`This message repeated ${g.repeatCount} times in a row`"
              data-testid="agent-message-repeat-count"
              >×{{ g.repeatCount }}</span
            >
            <span
              v-if="g.message.role === 'agent' && g.message.context_tokens != null"
              class="tabular-nums"
              :title="`~${g.message.context_tokens.toLocaleString()} tokens in context this turn`"
              data-testid="agent-message-context-tokens"
              >~{{ formatTokens(g.message.context_tokens) }} ctx</span
            >
            <span
              v-if="g.message.role === 'agent' && g.message.cost_cents != null"
              class="tabular-nums"
              title="Estimated cost of this turn"
              data-testid="agent-message-cost"
              >{{ formatCost(g.message.cost_cents) }}</span
            >
          </div>
          <button
            v-if="g.message.role === 'agent'"
            type="button"
            class="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:text-white/80"
            aria-label="Copy agent message"
            data-testid="agent-message-copy"
            @click="copyMessage(g.message.text)"
          >
            Copy
          </button>
        </div>
      </div>
    </div>

    <!-- Live status bubble: mirrors an agent turn bubble but uses italic
         dimmed text to signal the turn is still in progress. Updates in place
         as each progress broadcast lands; disappears when the final turn
         arrives and takes its place in the committed message list. -->
    <div v-if="agentRunning" class="flex justify-start" data-testid="agent-running">
      <div
        class="max-w-[85%] rounded-xl px-3 py-2 text-sm bg-white/5 border border-white/10 text-white/50 flex items-center gap-2"
        data-testid="agent-live-bubble"
      >
        <span class="inline-block w-1.5 h-4 rounded-sm bg-brand-primary/70 animate-pulse shrink-0" aria-hidden="true" />
        <span class="italic" data-testid="agent-live-status">{{ liveStatus ?? 'Starting…' }}</span>
      </div>
    </div>
  </div>
</template>
